<div class="termination-dialog">
  <div class="termination-dialog__header">
    <div>
      <div class="termination-dialog__eyebrow">{{ reviewLabel }}</div>
      <h2>{{ data.employeeIdentity.name }}</h2>
      @if (data.employeeIdentity.email) {
        <p>{{ data.employeeIdentity.email }}</p>
      }
    </div>
    <button mat-icon-button type="button" aria-label="Close termination review" (click)="close()">
      <mat-icon>close</mat-icon>
    </button>
  </div>

  @if (!isProcessingAvailable) {
    <div class="termination-dialog__preview-notice">
      <mat-icon>visibility</mat-icon>
      <span>Preview only — no changes have been made</span>
    </div>
  }
  @if (isProcessingAvailable) {
    <div class="termination-dialog__preview-notice">
      <mat-icon>fact_check</mat-icon>
      <span>Guided review — team removals still use the existing member Remove action</span>
    </div>
  }

  <mat-divider></mat-divider>

  <div class="termination-dialog__content">
    @if (isLoading) {
      <div class="termination-dialog__loading">
        <mat-spinner [diameter]="28"></mat-spinner>
        <span>Loading review details</span>
      </div>
    }

    @if (!isLoading && loadError) {
      <div class="termination-dialog__error" role="status">
        <div>
          <mat-icon>info_outline</mat-icon>
          <span>{{ loadError }}</span>
        </div>
        @if (canRetry) {
          <button
            mat-stroked-button
            type="button"
            [disabled]="isLoading"
            (click)="retryReview()"
            >
            Retry
          </button>
        }
      </div>
    }

    @if (!isLoading && !loadError) {
      <section class="review-section">
        <h3>Termination evidence</h3>
        <dl class="review-grid">
          <div><dt>Provider</dt><dd>{{ terminationCase.providerLabel }}</dd></div>
          <div><dt>Review state</dt><dd>{{ reviewLabel }}</dd></div>
          <div><dt>Effective termination date</dt><dd>{{ (terminationCase.effectiveTerminationDate | date:'mediumDate':'UTC') || 'Not available' }}</dd></div>
          <div><dt>Match status</dt><dd>{{ statusLabel(terminationCase.matchStatus) }}</dd></div>
          <div><dt>Employment status</dt><dd>{{ statusLabel(terminationCase.employmentStatus) }}</dd></div>
          <div><dt>Payroll participation</dt><dd>{{ statusLabel(terminationCase.payrollParticipationStatus) }}</dd></div>
          @if (terminationCase.safeErrorCode) {
            <div class="review-grid__wide">
              <dt>Review code</dt><dd>{{ terminationCase.safeErrorCode }}</dd>
            </div>
          }
        </dl>
        @if (requiresManualReview) {
          <div class="manual-review-note">
            <mat-icon>rule</mat-icon>
            <span>Manual review is required before any future processing decision.</span>
          </div>
        }
      </section>
      @if (impactPreview; as preview) {
        <section class="review-section">
          <h3>Company memberships ({{ preview.memberships.count }})</h3>
          @if (preview.memberships.matchRequired) {
            <p class="section-help">
              No Wallit employee is linked to this case. Membership impact cannot be resolved automatically.
            </p>
          }
          @if (preview.memberships.items.length) {
            <div class="evidence-list">
              @for (membership of preview.memberships.items; track membership) {
                <div class="evidence-row">
                  <div>
                    <strong>{{ teamName(membership.teamId) }}</strong>
                    <span>{{ membership.status || 'Status unavailable' }}</span>
                  </div>
                  <span>{{ membership.disabled ? 'Disabled membership' : 'Current membership' }}</span>
                </div>
              }
            </div>
          } @else {
            <p class="empty-evidence">No exact-company memberships were found.</p>
          }
        </section>
      }
      @if (impactPreview; as preview) {
        <section class="review-section">
          <h3>Active monthly allowance participation ({{ preview.monthlyAllowanceParticipation.count }})</h3>
          <p class="section-help">Active monthly allowance participation stored for this company.</p>
          @if (preview.monthlyAllowanceParticipation.items.length) {
            <div class="evidence-list">
              @for (allowance of preview.monthlyAllowanceParticipation.items; track allowance) {
                <div class="evidence-row">
                  <div>
                    <strong>{{ teamName(allowance.teamId) }}</strong>
                    <span>{{ statusLabel(allowance.status) }}</span>
                  </div>
                  <span>{{ allowance.activatedAt ? (allowance.activatedAt | date:'mediumDate':'UTC') : 'Date unavailable' }}</span>
                </div>
              }
            </div>
          } @else {
            <p class="empty-evidence">No active monthly allowance participation was found.</p>
          }
        </section>
      }
      @if (impactPreview; as preview) {
        <section class="review-section">
          <h3>Pending settlement evidence</h3>
          <div class="evidence-callout">
            <strong>Stored evidence only</strong>
            <span>Live Stripe ACH status was not checked.</span>
          </div>
          @if (preview.pendingSettlement.teams.length) {
            <div class="evidence-list">
              @for (settlement of preview.pendingSettlement.teams; track settlement) {
                <div class="evidence-row">
                  <div>
                    <strong>{{ teamName(settlement.teamId) }}</strong>
                    <span>{{ settlement.knownPendingSettlement ? 'Stored pending indicator found' : 'No stored pending indicator' }}</span>
                  </div>
                  <span>{{ settlement.pendingFundingSnapshotCount }} pending funding snapshot(s)</span>
                </div>
              }
            </div>
          } @else {
            <p class="empty-evidence">No team-level stored pending-settlement indicators were found.</p>
          }
        </section>
      }
      @if (impactPreview; as preview) {
        <section class="review-section">
          <h3>Payroll deduction evidence</h3>
          <div class="evidence-callout evidence-callout--manual">
            <strong>Current deduction obligation: Unknown</strong>
            <span>Stored evidence is for manual review and does not establish an active cancellation obligation.</span>
          </div>
          @if (preview.payrollDeductions.items.length) {
            <div class="evidence-list">
              @for (deduction of preview.payrollDeductions.items; track deduction) {
                <div class="evidence-row">
                  <div>
                    <strong>{{ evidenceRecordLabel(deduction.recordType) }}</strong>
                    <span>{{ identityEvidenceLabel(deduction.identityEvidence) }}</span>
                  </div>
                  <span>{{ teamName(deduction.teamId) }}</span>
                </div>
              }
            </div>
          } @else {
            <p class="empty-evidence">No stored payroll-deduction evidence was found. Current obligation remains unknown.</p>
          }
        </section>
      }
      @if (data.processingEnabled) {
        <section class="review-section processing-section">
          <h3>Guided processing</h3>
          @if (processingLoadError) {
            <div class="termination-dialog__error" role="status">
              <div><mat-icon>info_outline</mat-icon><span>{{ processingLoadError }}</span></div>
              @if (!isProcessingUnavailable) {
                <button
                  mat-stroked-button
                  type="button"
                  [disabled]="isLoading"
                  (click)="retryReview()"
                >Refresh</button>
              }
            </div>
          }
          @if (processingReadiness; as readiness) {
            @if (readiness.payrollDeductionCancellation.state === 'actionable') {
              <div class="evidence-callout">
                <strong>One payroll deduction can be canceled through the guided UKG Ready flow</strong>
                <span>The cancellation is effective today, cancels the associated Wallit subscription, and does not issue a refund.</span>
              </div>
            }
            @if (readiness.payrollDeductionCancellation.state === 'submitted' || deductionCancellationStatus) {
              <div class="evidence-callout">
                <strong>Cancellation submitted</strong>
                <span>UKG Ready accepted the cancellation submission and the associated Wallit subscription was canceled. This is not an independent UKG verification.</span>
              </div>
            }
            @if (canReviewTeamRemovals) {
              <div class="evidence-callout">
                <strong>{{ readiness.membershipCounts.remainingMembershipCount }} company membership(s) still require review</strong>
                <span>Use Remove on each highlighted Payroll Teams row. The final membership asks whether the employee is leaving the company.</span>
              </div>
            }
            @if (readiness.state === 'awaiting_final_removal') {
              <div class="evidence-callout evidence-callout--manual">
                <strong>Final removal is still required</strong>
                <span>Settlement must clear before the existing Finalize Removal action can succeed.</span>
              </div>
            }
            @if (readiness.blockers.length) {
              <div class="evidence-list">
                @for (processingBlocker of readiness.blockers; track processingBlocker) {
                  <div class="evidence-row">
                    <div><strong>{{ processingBlocker.message }}</strong></div>
                    <span>{{ processingBlocker.code }}</span>
                  </div>
                }
              </div>
            }
            @if (readiness.state === 'ready_to_complete') {
              <div class="evidence-callout">
                <strong>Required local outcomes verified</strong>
                <span>The termination review can now be completed explicitly.</span>
              </div>
            }
          }
        </section>
      }
    }
  </div>

  <mat-divider></mat-divider>
  <div class="termination-dialog__footer">
    <button mat-raised-button color="primary" type="button" (click)="close()">Close</button>
    @if (canMarkResolved) {
      <button
        class="mark-resolved-button"
        mat-stroked-button type="button"
        [disabled]="isResolving || isResolveConfirmationPending"
        (click)="markResolved()"
      >{{ isResolving ? 'Marking resolved…' : 'Mark resolved' }}</button>
    }
    @if (canCancelPayrollDeduction) {
      <button
        mat-stroked-button type="button"
        [disabled]="isCancellingDeduction || isDeductionCancellationConfirmationPending"
        (click)="cancelPayrollDeduction()"
      >{{ isCancellingDeduction ? 'Submitting cancellation…' : 'Cancel payroll deduction' }}</button>
    }
    @if (canReviewTeamRemovals) {
      <button
        mat-raised-button color="primary" type="button"
        (click)="reviewTeamRemovals()"
      >Review team removals</button>
    }
    @if (canCompleteReview) {
      <button
        mat-raised-button color="primary" type="button"
        [disabled]="isCompleting"
        (click)="completeReview()"
      >{{ isCompleting ? 'Completing…' : 'Complete termination review' }}</button>
    }
  </div>
</div>
